import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AsYouType, parsePhoneNumber } from 'libphonenumber-js/mobile';
import { Warning } from './Warning';
import { fetchLoggedIn, getConferenceList, login, logout, showUsageTerms, useApp } from '../store';
import { Loader } from './Loader';

interface Conference {
  msisdn: string;
  konferanse: string;
}

export function Login() {
  const { t, i18n } = useTranslation();
  const { state, dispatch } = useApp();
  const [countryCode, setCountryCode] = useState<'NO' | 'SE' | 'DK'>('NO');
  const [warning, setWarning] = useState<string[]>([]);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [conference, setConference] = useState<string | null>(null);
  const [gdpr, setGDPR] = useState(false);

  useEffect(() => {
    fetchLoggedIn(dispatch);
    getConferenceList(dispatch);

    // Check localStorage for saved language preference
    const savedLanguage = localStorage.getItem('language');
    if (savedLanguage && (savedLanguage === 'NO' || savedLanguage === 'SE' || savedLanguage === 'DK')) {
      setCountryCode(savedLanguage as 'NO' | 'SE' | 'DK');
    } else {
      // Auto-detect from browser
      const cc = navigator.language.split('-');
      if (cc[1] && (cc[1] === 'NO' || cc[1] === 'SE' || cc[1] === 'DK')) {
        setCountryCode(cc[1] as 'NO' | 'SE' | 'DK');
      }
    }
  }, [dispatch]);

  useEffect(() => {
    i18n.changeLanguage(countryCode);
  }, [countryCode, i18n]);

  const handleLogin = () => {
    if (phoneNumber === '' || conference == null) {
      setWarning([t('warningValidatePhoneAndConference')]);
      return;
    }

    if (gdpr === true) {
      try {
        const phone = parsePhoneNumber(phoneNumber, countryCode);
        if (phone?.isValid()) {
          login(phone.number.substring(1), conference, dispatch);
        } else {
          throw new Error('Not valid');
        }
      } catch (e) {
        setWarning([t('warningPhoneNumber')]);
      }
    } else {
      setWarning([t('warningGdprConsent')]);
    }
  };

  const handlePhoneChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setWarning([]);
    setPhoneNumber(new AsYouType(countryCode).input(event.target.value));

    try {
      const phone = parsePhoneNumber(phoneNumber, countryCode);
      if (phone?.country && countryCode !== phone.country) {
        if (phone.country === 'NO' || phone.country === 'SE' || phone.country === 'DK') {
          setCountryCode(phone.country);
          localStorage.setItem('language', phone.country);
        }
      }
    } catch (e) {
      // Ignore parsing errors while typing
    }
  };

  const showUsageRights = () => {
    showUsageTerms(true, dispatch);
  };

  const handleLogout = () => {
    logout(dispatch);
  };

  const handleGDPR = (event: React.ChangeEvent<HTMLInputElement>) => {
    setWarning([]);
    setGDPR(event.currentTarget.checked);
  };

  const handleCountrySelect = (code: 'NO' | 'SE' | 'DK') => {
    setWarning([]);
    setCountryCode(code);
    localStorage.setItem('language', code);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleLogin();
    }
  };

  if (state.isLoggedIn) {
    return null;
  }

  if (state.loading) {
    return <Loader />;
  }

  return (
    <>
      <div className="logout_menu">
        <button className="logout" onClick={handleLogout}>
          &#10005;
        </button>
      </div>
      <Warning message={warning} />
      <div className="phoneInput">
        <select
          value={countryCode}
          onChange={(e) => handleCountrySelect(e.target.value as 'NO' | 'SE' | 'DK')}
          style={{ padding: '4px 8px', fontSize: '28px', borderRadius: '8px 0 0 8px', border: 'none' }}
        >
          <option value="NO">🇳🇴</option>
          <option value="SE">🇸🇪</option>
          <option value="DK">🇩🇰</option>
        </select>

        <input
          type="tel"
          placeholder={t('phonenumber')}
          value={phoneNumber}
          id={'telefonnummer'}
          onChange={handlePhoneChange}
          onKeyDown={handleKeyDown}
        />
      </div>
      <select
        className={`select-css ${conference ? '' : 'placeholder'}`}
        defaultValue=""
        onChange={(e) => {
          setConference(e.currentTarget.value);
        }}
        onKeyDown={handleKeyDown}
      >
        <option value="" disabled>{t('chooseConference')}</option>
        {(state.conferenceList as Conference[])
          .sort((a, b) => (a.konferanse > b.konferanse ? 1 : -1))
          .map((e) => (
            <option key={e.msisdn} value={e.msisdn}>
              {e.konferanse}
            </option>
          ))}
      </select>

      <label className={'container'}>
        {t('GdprConsent')}
        <input type={'checkbox'} value={'accept'} onChange={handleGDPR} />
        <span className="checkmark"></span>
      </label>

      <button className="primary" onClick={handleLogin}>
        {t('loginButton')}
      </button>
      <div className={'bottom'}>
        <button className={'link-button'} onClick={showUsageRights}>
          {t('readPrivacyPolicy')}
        </button>
      </div>
    </>
  );
}
